import React from "react";
import { Percent, Handshake, WarningCircle } from "@phosphor-icons/react";

import { ConsoleShell } from "../../../design-system/components/shells";
import {
  Button,
  Empty,
  Field,
  Input,
  LoadingBlock,
  PageTitle,
  Switch,
  SwitchRow,
} from "../../../design-system/components/desktop";
import {
  Input as DsInput,
  Field as DsField,
  Cta,
  SwitchRow as DsSwitchRow,
} from "../../../design-system/components/mobile";
import { useViewport } from "../../../design-system/hooks/useViewport";
import {
  Panel,
  PanelStack,
} from "../../../design-system/patterns/settings";
import { errorText, LOAD_FAILURE } from "../../../utils/errorText";

/**
 * /admin-system-settings, the view. Pure.
 *
 * WHY THIS IS NOT THE `settings.html` TEMPLATE EITHER
 * --------------------------------------------------
 * The template is a two-column settings page with a left nav. There are exactly
 * TWO settings here and there is no second page to navigate to, so a nav rail
 * would be a control with one destination. What this page does keep from the
 * template, and what /profile deliberately does not, is the SAVE BAR: the
 * platform fee is a text field, a text field does not commit itself, and a
 * page-wide change that affects every transaction on the marketplace should not
 * commit on blur.
 *
 * THE SWITCH IS THE EXCEPTION AND IT IS DELIBERATE
 * -----------------------------------------------
 * `Switch`'s own contract is that a switch commits immediately and therefore
 * never sits beside a Save button for the same value. Here it does sit beside
 * one, because both settings post through a single `updateSystemSettings` call
 * and splitting them into two endpoints is a backend change, not a design one.
 * So the switch is presented as a FORM CONTROL: its state is pending until Save
 * is pressed, and the helper line under it says so. This is the honest reading
 * of the API we have, and it is flagged rather than hidden.
 */

export function SystemSettingsView({
  platformFee = 5,
  onPlatformFeeChange,
  sellInPerson = false,
  onSellInPersonChange,
  loading = false,
  saving = false,
  error,
  onSave,
}) {
  const { isDesktop } = useViewport();
  const busy = loading || saving;

  const FieldControl = isDesktop ? Field : DsField;
  const TextControl = isDesktop ? Input : DsInput;
  const Commit = isDesktop ? Button : Cta;
  const commitProps = isDesktop
    ? { variant: "primary" }
    : { variant: "solid", block: true };

  return (
    <ConsoleShell
      variant="admin"
      title="System settings"
      wide={false}
      breadcrumbs={[{ label: "Admin" }, { label: "System settings" }]}
    >
      <PageTitle>System settings</PageTitle>

      {loading ? (
        <LoadingBlock>Loading system settings</LoadingBlock>
      ) : (
        <PanelStack>
          {error ? (
            <Empty
              icon={WarningCircle}
              illustration="request-failed"
              tone="error"
              title="We could not load the system settings"
              text={errorText(error, LOAD_FAILURE)}
            />
          ) : null}

          <Panel
            icon={Percent}
            title="Platform fee"
            sub="Charged on every transaction, marketplace wide."
          >
            <FieldControl
              label="Platform fee"
              hint="A whole or decimal percentage between 0 and 100."
              htmlFor="platform-fee"
            >
              <TextControl
                id="platform-fee"
                type="number"
                min="0"
                max="100"
                step="0.01"
                inputMode="decimal"
                value={platformFee}
                onChange={(e) => onPlatformFeeChange(e.target.value)}
                disabled={busy}
              />
            </FieldControl>
          </Panel>

          <Panel
            icon={Handshake}
            title="Selling in person"
            sub="Lets a buyer collect from a seller instead of having an order delivered."
          >
            {isDesktop ? (
              <SwitchRow
                title="Enable selling in person"
                sub="Takes effect for every seller once you save."
              >
                <Switch
                  checked={sellInPerson}
                  onChange={(e) => onSellInPersonChange(e.target.checked)}
                  disabled={busy}
                />
              </SwitchRow>
            ) : (
              <DsSwitchRow
                label="Enable selling in person"
                sub="Takes effect for every seller once you save."
                checked={sellInPerson}
                disabled={busy}
                onCheckedChange={onSellInPersonChange}
              />
            )}
          </Panel>

          <div className="sysx-save">
            <Commit {...commitProps} onClick={onSave} disabled={busy}>
              {saving ? "Saving" : "Save settings"}
            </Commit>
          </div>
        </PanelStack>
      )}
    </ConsoleShell>
  );
}

export default SystemSettingsView;
